import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { useNavigate } from 'react-router-dom';
import bgVideo from './images/bg.mp4';

const HeroSection = () => {
  const heroRef = useRef();
  const titleRef = useRef();
  const subtitleRef = useRef();
  const ctaRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const tl = gsap.timeline();
    
    tl.fromTo(titleRef.current, 
      { y: 100, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.2, ease: "power3.out" }
    )
    .fromTo(subtitleRef.current,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, ease: "power3.out" },
      "-=0.8"
    )
    .fromTo(ctaRef.current,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
      "-=0.6"
    );
  }, []);

  const handleShopNow = () => {
    // Scroll to Featured Products section
    const featuredSection = document.querySelector('[data-section="featured-products"]');
    if (featuredSection) {
      featuredSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleExploreCollection = () => {
    // Navigate to store page
    navigate('/store');
  };

  return (
    <HeroContainer ref={heroRef}>
      <VideoBackground autoPlay muted loop>
        <source src={bgVideo} type="video/mp4" />
      </VideoBackground>
      
      <HeroOverlay />
      
      <HeroContent>
        <HeroTitle ref={titleRef}>
          JUST DO IT
        </HeroTitle>
        
        <HeroSubtitle ref={subtitleRef}>
         <b><i> Bring inspiration and innovation to every athlete* in the world.</i></b>
          <br />
          <SmallText>*If you have a body, you are an athlete.</SmallText>
        </HeroSubtitle>
        
        <CTAButtons ref={ctaRef}>
          <PrimaryButton onClick={handleShopNow}>Shop Now</PrimaryButton>
          <SecondaryButton onClick={handleExploreCollection}>Explore Collection</SecondaryButton>
        </CTAButtons>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;

const HeroContainer = styled.section`
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #000;
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  transform: translate(-50%, -50%);
  z-index: 1;
  object-fit: cover;
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-family: 'Anton', sans-serif;
  font-size: clamp(3rem, 8vw, 8rem);
  font-weight: 900;
  font-style: italic;
  margin: 0 0 24px 0;
  letter-spacing: 4px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1.1rem, 2.5vw, 1.5rem);
  line-height: 1.6;
  margin: 0 0 40px 0;
  font-weight: 300;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const SmallText = styled.span`
  font-size: 0.9em;
  font-style: italic;
  opacity: 0.8;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const BaseButton = styled.button`
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  min-width: 180px;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  @media (max-width: 480px) {
    min-width: 250px;
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: #fff;
  color: #000;
  
  &:hover {
    background: #f0f0f0;
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.2);
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: transparent;
  color: #fff;
  border: 2px solid #fff;
  
  &:hover {
    background: #fff;
    color: #000;
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.2);
  }
`;
